import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
    type ReactNode,
} from 'react'
import { createViewThruSdk, loadViewThruSdkScript } from './loadViewThruSdk'
import type { ViewThruDeviceContextValue, ViewThruEnvironment, ViewThruVerificationData } from './types'

const defaultContext: ViewThruDeviceContextValue = {
    deviceSessionId: null,
    originToken: null,
    deviceCollectionCompleted: false,
    isCollecting: false,
    isReady: false,
    refreshVerificationData: async () => null,
}

const ViewThruDeviceContext = createContext<ViewThruDeviceContextValue>(defaultContext)

const logVerificationData = (label: string, data: ViewThruVerificationData) => {
    console.log(`[ViewThru] ${label}:`, {
        deviceSessionId: data.deviceSessionId,
        deviceCollectionCompleted: data.deviceCollectionCompleted,
        originTokenPresent: Boolean(data.originToken),
        originTokenPreview: data.originToken ? `${data.originToken.slice(0, 8)}…` : null,
    })
}

export interface ViewThruDeviceProviderProps {
    environment?: ViewThruEnvironment
    enabled?: boolean
    children: ReactNode
}

const applyVerificationData = (
    data: ViewThruVerificationData,
    setters: {
        setDeviceSessionId: (v: string) => void
        setOriginToken: (v: string) => void
        setDeviceCollectionCompleted: (v: boolean) => void
    }
) => {
    setters.setDeviceSessionId(data.deviceSessionId)
    setters.setOriginToken(data.originToken)
    setters.setDeviceCollectionCompleted(data.deviceCollectionCompleted)
}

export function ViewThruDeviceProvider({
    environment = 'test',
    enabled = true,
    children,
}: ViewThruDeviceProviderProps) {
    const sdkRef = useRef<ReturnType<typeof createViewThruSdk> | null>(null)
    const initializedRef = useRef(false)
    const [deviceSessionId, setDeviceSessionId] = useState<string | null>(null)
    const [originToken, setOriginToken] = useState<string | null>(null)
    const [deviceCollectionCompleted, setDeviceCollectionCompleted] = useState(false)
    const [isCollecting, setIsCollecting] = useState(false)
    const [isReady, setIsReady] = useState(!enabled)

    const refreshVerificationData = useCallback(async (): Promise<ViewThruVerificationData | null> => {
        if (!enabled || !sdkRef.current) return null
        setIsCollecting(true)
        try {
            const data = await sdkRef.current.getVerificationData()
            applyVerificationData(data, {
                setDeviceSessionId,
                setOriginToken,
                setDeviceCollectionCompleted,
            })
            setIsReady(true)
            logVerificationData('refresh verification', data)
            return data
        } catch (error) {
            console.error('[ViewThru] refresh failed:', error)
            return null
        } finally {
            setIsCollecting(false)
        }
    }, [enabled])

    useEffect(() => {
        if (!enabled) {
            setIsReady(true)
            return
        }

        if (initializedRef.current) return
        initializedRef.current = true

        let cancelled = false

        const boot = async () => {
            setIsCollecting(true)
            try {
                await loadViewThruSdkScript(environment)
                if (cancelled) return
                sdkRef.current = createViewThruSdk(environment)
                const data = await sdkRef.current.getVerificationData()
                if (cancelled) return
                applyVerificationData(data, {
                    setDeviceSessionId,
                    setOriginToken,
                    setDeviceCollectionCompleted,
                })
                setIsReady(true)
                logVerificationData('boot verification', data)
            } catch (error) {
                console.error('[ViewThru] boot failed:', error)
                if (!cancelled) setIsReady(false)
            } finally {
                if (!cancelled) setIsCollecting(false)
            }
        }

        void boot()

        return () => {
            cancelled = true
        }
    }, [enabled, environment])

    return (
        <ViewThruDeviceContext.Provider
            value={{
                deviceSessionId,
                originToken,
                deviceCollectionCompleted,
                isCollecting,
                isReady,
                refreshVerificationData,
            }}
        >
            {children}
        </ViewThruDeviceContext.Provider>
    )
}

export function useViewThruDevice(): ViewThruDeviceContextValue {
    return useContext(ViewThruDeviceContext)
}
